import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';

const PLANO_COR = { bronze: '#b45309', prata: '#6b7280', ouro: '#d97706', diamante: '#7c3aed' };
const PLANO_BG  = { bronze: '#fef3c7', prata: '#f3f4f6', ouro: '#fffbeb', diamante: '#f5f3ff' };

const FEATURES_LABEL = {
  financeiro:          'Módulo Financeiro',
  sinapi:              'SINAPI integrado',
  controle_usuarios:   'Controle de usuários',
  exportar_csv:        'Exportar CSV',
  suporte_prioritario: 'Suporte prioritário',
};

function fmtData(v) {
  if (!v) return '—';
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const [y, m, d] = s.split('T')[0].split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
}

function parseDataLocal(v) {
  if (!v) return null;
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const [y, m, d] = s.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function fmtCartao(v) {
  return v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}
function fmtCPF(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.slice(0, 3) + '.' + d.slice(3);
  if (d.length <= 9) return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6);
  return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6, 9) + '-' + d.slice(9);
}
function detectarBandeira(numero) {
  const n = numero.replace(/\s/g, '');
  if (/^4/.test(n)) return 'visa';
  if (/^5[1-5]/.test(n)) return 'master';
  if (/^3[47]/.test(n)) return 'amex';
  if (/^606282/.test(n)) return 'hipercard';
  if (/^(636368|438935|504175|451416|636297|5067|4576|4011)/.test(n)) return 'elo';
  return null;
}
function fmtValidade(v) {
  const d = v.replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? d.slice(0, 2) + '/' + d.slice(2) : d;
}

function BarraUso({ uso, limite, cor }) {
  if (!limite) return null;
  const pct = Math.min((uso / limite) * 100, 100);
  const corBarra = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : cor;
  return (
    <div style={{ height: 8, background: 'var(--border-color)', borderRadius: 4, marginTop: 8, overflow: 'hidden' }}>
      <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: corBarra, transition: 'width 0.4s' }} />
    </div>
  );
}

function ModalPix({ pagamento, onConcluido, onFechar }) {
  const [copiado, setCopiado] = useState(false);
  const [status, setStatus] = useState('pendente');

  useEffect(() => {
    const intervalo = setInterval(async () => {
      try {
        const r = await api.get(`/tenant/verificar-renovacao/${pagamento.payment_id}`);
        if (r.data.status === 'concluido') {
          setStatus('concluido');
          clearInterval(intervalo);
          setTimeout(onConcluido, 1500);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(intervalo);
  }, [pagamento.payment_id, onConcluido]);

  function copiar() {
    navigator.clipboard.writeText(pagamento.qr_code);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
        {status === 'concluido' ? (
          <>
            <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
            <h3 style={{ marginBottom: 8 }}>Pagamento confirmado!</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Redirecionando...</p>
          </>
        ) : (
          <>
            <h3 style={{ marginBottom: 4 }}>Pague via PIX</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: pagamento.credito > 0 ? 8 : 16 }}>
              Plano <strong style={{ textTransform: 'capitalize' }}>{pagamento.plano_nome}</strong>:{' '}
              R$ {parseFloat(pagamento.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            {pagamento.credito > 0 && (
              <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, textAlign: 'left' }}>
                <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>
                  Valor cheio do plano: <span style={{ textDecoration: 'line-through' }}>R$ {parseFloat(pagamento.valor_cheio).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div style={{ color: '#16a34a', marginBottom: 4 }}>
                  Crédito de {pagamento.dias_restantes} dias do plano atual: − R$ {parseFloat(pagamento.credito).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                  Valor a pagar agora: R$ {parseFloat(pagamento.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>
            )}
            {pagamento.qr_code_base64 && (
              <img src={`data:image/png;base64,${pagamento.qr_code_base64}`} alt="QR Code PIX"
                style={{ width: 200, height: 200, margin: '0 auto 16px', display: 'block', borderRadius: 8 }} />
            )}
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 12px', fontSize: 11, wordBreak: 'break-all', color: 'var(--text-secondary)', marginBottom: 12, textAlign: 'left' }}>
              {pagamento.qr_code}
            </div>
            <button className="btn-primary" style={{ width: '100%', marginBottom: 8 }} onClick={copiar}>
              {copiado ? '✓ Copiado!' : 'Copiar código PIX'}
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Aguardando pagamento... A confirmação é automática.
            </p>
            <button className="btn-secondary" style={{ width: '100%' }} onClick={onFechar}>Cancelar</button>
          </>
        )}
      </div>
    </div>
  );
}

function Plano() {
  const [plano, setPlano] = useState(null);
  const [planosDisponiveis, setPlanosDisponiveis] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [cancelando, setCancelando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [multaFidelidade, setMultaFidelidade] = useState(null); // { meses_restantes, multa, mensagem }
  const [multaTroca, setMultaTroca] = useState(null); // { meses_restantes, multa, mensagem } - troca de plano anual -> mensal
  const [forcarMensal, setForcarMensal] = useState(false); // veio do botão "Mudar para mensal", esconde o toggle

  // Modal de pagamento (upgrade / reativação)
  const [modalPagamento, setModalPagamento] = useState(null); // { planoid, nome, preco }
  const [cicloEscolhido, setCicloEscolhido] = useState('mensal'); // 'mensal' | 'anual'
  const [telaCartao, setTelaCartao] = useState(false);
  const [dadosCartao, setDadosCartao] = useState({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
  const [erroCartao, setErroCartao] = useState('');
  const [processando, setProcessando] = useState(false);
  const [pagamentoPix, setPagamentoPix] = useState(null);
  const [mpPronto, setMpPronto] = useState(!!window.MercadoPago);

  // Modal de downgrade
  const [modalDowngrade, setModalDowngrade] = useState(null); // { planoid, nome, preco }
  const [processandoDowngrade, setProcessandoDowngrade] = useState(false);
  const [erroDowngrade, setErroDowngrade] = useState('');

  // Modal de sucesso
  const [modalSucesso, setModalSucesso] = useState(null); // { titulo, texto, plano }

  // Cálculo de rateio do upgrade
  const [calculoUpgrade, setCalculoUpgrade] = useState(null); // { valor_cheio, credito, dias_restantes, valor_cobrar }

  const [modalCredito, setModalCredito] = useState(false);
  const [quantidadeCredito, setQuantidadeCredito] = useState(1);
  const [metodoCredito, setMetodoCredito] = useState('cartao');
  const [dadosCartaoCredito, setDadosCartaoCredito] = useState({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
  const [erroCredito, setErroCredito] = useState('');
  const [processandoCredito, setProcessandoCredito] = useState(false);
  const [pixCredito, setPixCredito] = useState(null);

  // Renovação de fidelidade anual vencida
  const [etapaRenovacaoAnual, setEtapaRenovacaoAnual] = useState(null); // 'anual' | 'mensal' | null
  const [processandoRenovacaoAnual, setProcessandoRenovacaoAnual] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [rStatus, rPlanos] = await Promise.all([
        api.get('/tenant/status'),
        api.get('/tenant/planos'),
      ]);
      setPlano(rStatus.data);
      setPlanosDisponiveis(rPlanos.data);
    } catch {}
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const mostrouRenovacaoAnualRef = useRef(false);
  useEffect(() => {
    if (plano?.renovacao_anual_pendente && !mostrouRenovacaoAnualRef.current) {
      mostrouRenovacaoAnualRef.current = true;
      setEtapaRenovacaoAnual('anual');
    }
  }, [plano]);

  async function confirmarRenovacaoAnual() {
    setProcessandoRenovacaoAnual(true);
    try {
      const r = await api.post('/tenant/renovacao-anual/confirmar');
      setEtapaRenovacaoAnual(null);
      setModalSucesso({ titulo: 'Renovação confirmada!', texto: r.data.mensagem });
      carregar();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao confirmar renovação.');
    }
    setProcessandoRenovacaoAnual(false);
  }

  async function confirmarTrocaParaMensalPosFidelidade() {
    setProcessandoRenovacaoAnual(true);
    try {
      const r = await api.post('/tenant/renovacao-anual/mensal');
      setEtapaRenovacaoAnual(null);
      setModalSucesso({ titulo: 'Plano alterado!', texto: r.data.mensagem });
      carregar();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao alterar plano.');
    }
    setProcessandoRenovacaoAnual(false);
  }

  async function cancelarPosFidelidade() {
    setProcessandoRenovacaoAnual(true);
    try {
      const r = await api.post('/tenant/cancelar', {});
      setEtapaRenovacaoAnual(null);
      setModalSucesso({ titulo: 'Assinatura cancelada', texto: r.data.mensagem });
      carregar();
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao cancelar assinatura.');
    }
    setProcessandoRenovacaoAnual(false);
  }

  useEffect(() => {
    if (window.MercadoPago) { setMpPronto(true); return; }
    const script = document.createElement('script');
    script.src = 'https://sdk.mercadopago.com/js/v2';
    script.onload = () => setMpPronto(true);
    document.head.appendChild(script);
  }, []);

  async function handleCancelar(confirmarMulta) {
    setCancelando(true);
    try {
      let token_cartao;
      if (confirmarMulta) {
        const { numero, nome, validade, cvv, cpf } = dadosCartao;
        if (numero.replace(/\s/g, '').length < 13) { setErroCartao('Número de cartão inválido.'); setCancelando(false); return; }
        if (!nome.trim()) { setErroCartao('Informe o nome no cartão.'); setCancelando(false); return; }
        const [mes, ano] = validade.split('/');
        if (!mes || !ano || ano.length !== 2) { setErroCartao('Validade inválida. Use MM/AA.'); setCancelando(false); return; }
        if (cvv.length < 3) { setErroCartao('CVV inválido.'); setCancelando(false); return; }
        const cpfDigits = cpf.replace(/\D/g, '');
        if (cpfDigits.length !== 11) { setErroCartao('CPF inválido. Informe os 11 dígitos.'); setCancelando(false); return; }
        setErroCartao('');
        const tokenRes = await api.post('/tenant/card-token', {
          card_number: numero, expiration_month: mes, expiration_year: '20' + ano,
          security_code: cvv, cardholder_name: nome, cpf,
        });
        token_cartao = tokenRes.data.token;
      }
      const r = await api.post('/tenant/cancelar', confirmarMulta ? { confirmar: true, token_cartao } : {});
      if (r.data.requer_confirmacao) {
        setMultaFidelidade(r.data);
        setCancelando(false);
        return;
      }
      setPlano(p => ({ ...p, status: 'cancelado' }));
      setConfirmando(false);
      setMultaFidelidade(null);
      setDadosCartao({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
    } catch (err) {
      setErroCartao(err.response?.data?.mensagem || 'Erro ao cancelar assinatura.');
    }
    setCancelando(false);
  }

  const ehTrial = plano?.status === 'trial' || plano?.plano === 'gratis';
  const ehAnual = plano?.ciclo_cobranca === 'anual';
  const rotaPagamento = ehTrial ? '/tenant/assinar' : '/tenant/renovar';

  async function iniciarPix() {
    setProcessando(true);
    try {
      const r = await api.post(rotaPagamento, { planoid: modalPagamento.planoid, metodo: 'pix' });
      setPagamentoPix(r.data);
      setModalPagamento(null);
    } catch (err) {
      alert(err.response?.data?.mensagem || 'Erro ao iniciar pagamento.');
    }
    setProcessando(false);
  }

  async function iniciarCartao(multaJaConfirmada) {
    const { numero, nome, validade, cvv, cpf } = dadosCartao;
    if (numero.replace(/\s/g, '').length < 13) return setErroCartao('Número de cartão inválido.');
    if (!nome.trim()) return setErroCartao('Informe o nome no cartão.');
    const [mes, ano] = validade.split('/');
    if (!mes || !ano || ano.length !== 2) return setErroCartao('Validade inválida. Use MM/AA.');
    if (cvv.length < 3) return setErroCartao('CVV inválido.');
    const cpfDigits = cpf.replace(/\D/g, '');
    if (cpfDigits.length !== 11) return setErroCartao('CPF inválido. Informe os 11 dígitos.');
    setErroCartao('');
    setProcessando(true);
    try {
      const tokenRes = await api.post('/tenant/card-token', {
        card_number: numero,
        expiration_month: mes,
        expiration_year: '20' + ano,
        security_code: cvv,
        cardholder_name: nome,
        cpf,
      });
      const token = tokenRes.data.token;

      const bandeira = detectarBandeira(numero);
      const r = await api.post(rotaPagamento, {
        planoid: modalPagamento.planoid,
        metodo: 'cartao',
        token_cartao: token,
        ...(bandeira && { payment_method_id: bandeira }),
        ciclo: cicloEscolhido,
        ...(multaJaConfirmada && { multa_confirmada: true }),
      });
      if (r.data.requer_confirmacao_multa) {
        setMultaTroca(r.data);
        setProcessando(false);
        return;
      }
      if (r.data.status === 'concluido') {
        setModalPagamento(null);
        setTelaCartao(false);
        setDadosCartao({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
        setModalSucesso({
          titulo: 'Pagamento aprovado!',
          texto: `Seu plano ${r.data.plano_nome || ''} está ativo. Um e-mail de confirmação foi enviado.`,
        });
        carregar();
      } else if (r.data.status === 'pendente') {
        setModalPagamento(null);
        setTelaCartao(false);
        setDadosCartao({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
        setModalSucesso({
          titulo: 'Pagamento em análise',
          texto: 'Seu pagamento está sendo processado. Você receberá um e-mail assim que for confirmado.',
          pendente: true,
        });
        carregar();
      } else {
        setErroCartao('Aguardando confirmação do Mercado Pago. Verifique seu e-mail.');
      }
    } catch (err) {
      console.error('Erro cartão:', err);
      setErroCartao(err?.response?.data?.mensagem || err?.message || 'Erro ao processar cartão.');
    }
    setProcessando(false);
  }

  async function comprarCredito() {
    setProcessandoCredito(true);
    setErroCredito('');
    try {
      if (metodoCredito === 'pix') {
        const r = await api.post('/tenant/comprar-credito', { metodo: 'pix', quantidade: quantidadeCredito });
        if (r.data.status === 'concluido') {
          setModalCredito(false);
          setPixCredito(null);
          setQuantidadeCredito(1);
          setModalSucesso({
            titulo: 'Crédito adquirido!',
            texto: `${quantidadeCredito} crédito${quantidadeCredito > 1 ? 's' : ''} avulso${quantidadeCredito > 1 ? 's' : ''} adicionado${quantidadeCredito > 1 ? 's' : ''} à sua conta.`,
          });
          carregar();
        } else {
          setPixCredito(r.data);
        }
        setProcessandoCredito(false);
        return;
      }
      const { numero, nome, validade, cvv, cpf } = dadosCartaoCredito;
      if (numero.replace(/\s/g, '').length < 13) { setErroCredito('Número de cartão inválido.'); setProcessandoCredito(false); return; }
      if (!nome.trim()) { setErroCredito('Informe o nome no cartão.'); setProcessandoCredito(false); return; }
      const [mes, ano] = validade.split('/');
      if (!mes || !ano || ano.length !== 2) { setErroCredito('Validade inválida. Use MM/AA.'); setProcessandoCredito(false); return; }
      if (cvv.length < 3) { setErroCredito('CVV inválido.'); setProcessandoCredito(false); return; }
      const cpfDigits = cpf.replace(/\D/g, '');
      if (cpfDigits.length !== 11) { setErroCredito('CPF inválido. Informe os 11 dígitos.'); setProcessandoCredito(false); return; }

      const tokenRes = await api.post('/tenant/card-token', {
        card_number: numero, expiration_month: mes, expiration_year: '20' + ano,
        security_code: cvv, cardholder_name: nome, cpf,
      });
      const token = tokenRes.data.token;
      const bandeira = detectarBandeira(numero);
      await api.post('/tenant/comprar-credito', {
        metodo: 'cartao', token_cartao: token, quantidade: quantidadeCredito,
        ...(bandeira && { payment_method_id: bandeira }),
      });
      setModalCredito(false);
      setDadosCartaoCredito({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
      setQuantidadeCredito(1);
      setModalSucesso({
        titulo: 'Crédito adquirido!',
        texto: `${quantidadeCredito} crédito${quantidadeCredito > 1 ? 's' : ''} avulso${quantidadeCredito > 1 ? 's' : ''} adicionado${quantidadeCredito > 1 ? 's' : ''} à sua conta.`,
      });
      carregar();
    } catch (err) {
      setErroCredito(err.response?.data?.mensagem || 'Erro ao processar pagamento.');
    }
    setProcessandoCredito(false);
  }

  async function confirmarDowngrade() {
    setProcessandoDowngrade(true);
    try {
      const nomeDowngrade = modalDowngrade.nome;
      await api.post('/tenant/mudar-plano', { planoid: modalDowngrade.planoid });
      setModalDowngrade(null);
      setErroDowngrade('');
      setModalSucesso({
        titulo: 'Plano alterado!',
        texto: `Seu plano foi alterado para ${nomeDowngrade}. Um e-mail de confirmação foi enviado.`,
      });
      carregar();
    } catch (err) {
      setErroDowngrade(err.response?.data?.mensagem || 'Erro ao alterar plano.');
    }
    setProcessandoDowngrade(false);
  }

  if (carregando) return (
    <div><h2 style={{ marginBottom: 24 }}>💎 Meu Plano</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Carregando...</p>
    </div>
  );

  if (!plano) return (
    <div><h2 style={{ marginBottom: 24 }}>💎 Meu Plano</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Não foi possível carregar as informações do plano.</p>
    </div>
  );

  const cor = PLANO_COR[plano.plano] || '#7c3aed';
  const { obras, usuarios } = plano.limites;
  const suspensa = plano.status === 'suspenso' || plano.status === 'cancelado';

  const dataVenc = parseDataLocal(plano.data_vencimento);
  const diasParaExclusao = dataVenc && suspensa
    ? Math.max(0, 60 - Math.floor((new Date() - dataVenc) / (1000 * 60 * 60 * 24)))
    : null;
  const dataExclusao = dataVenc && suspensa
    ? new Date(dataVenc.getTime() + 60 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR')
    : null;

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>💎 Meu Plano</h2>

      {ehTrial && (
        <div style={{ background: '#78350f', border: '1px solid #d97706', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
          <p style={{ margin: 0, fontWeight: 700, color: '#fef3c7', fontSize: 15 }}>
            🎉 Você está no teste grátis
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#fde68a', lineHeight: 1.6 }}>
            Seu teste inclui <strong>1 obra</strong> e <strong>1 usuário</strong>, com acesso a todo o sistema.
            Escolha um plano abaixo e assine com cartão ou PIX para liberar mais obras, usuários e recursos.
          </p>
        </div>
      )}

      {suspensa && diasParaExclusao !== null && (
        <div style={{ background: diasParaExclusao <= 10 ? '#7f1d1d' : '#78350f', border: `1px solid ${diasParaExclusao <= 10 ? '#dc2626' : '#d97706'}`, borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
          <p style={{ margin: 0, fontWeight: 700, color: '#fef3c7', fontSize: 15 }}>
            ⚠️ Sua conta está {plano.status === 'cancelado' ? 'cancelada' : 'suspensa'}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#fde68a', lineHeight: 1.6 }}>
            Todos os seus dados serão <strong>deletados permanentemente</strong> em{' '}
            <strong>{diasParaExclusao} dias ({dataExclusao})</strong>.
            Renove seu plano agora para não perder nenhuma informação.
          </p>
        </div>
      )}

      {/* Plano atual */}
      <div className="card" style={{ marginBottom: 24, borderLeft: `4px solid ${cor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Plano atual</h3>
          <span style={{ background: PLANO_BG[plano.plano] || '#f5f3ff', color: cor, border: `1px solid ${cor}33`, borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 700, textTransform: 'capitalize' }}>{plano.plano}</span>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            R$ {parseFloat(plano.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês
          </span>
          {plano.ciclo_cobranca === 'anual' && (
            <span style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
              Anual (10% off)
            </span>
          )}
          {plano.data_vencimento && !suspensa && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
              Válido até {fmtData(plano.data_vencimento)}
            </span>
          )}
        </div>

        {!suspensa && (
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ minWidth: 160 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Obras ativas</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {obras.uso}<span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-muted)' }}>{obras.limite ? ` / ${obras.limite}` : ' / ilimitado'}</span>
              </div>
              <BarraUso uso={obras.uso} limite={obras.limite} cor={cor} />
              {obras.limite && obras.uso >= obras.limite && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Limite atingido</div>}
            </div>
            <div style={{ minWidth: 160 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Usuários ativos</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {usuarios.uso}<span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-muted)' }}>{usuarios.limite ? ` / ${usuarios.limite}` : ' / ilimitado'}</span>
              </div>
              <BarraUso uso={usuarios.uso} limite={usuarios.limite} cor={cor} />
              {usuarios.limite && usuarios.uso >= usuarios.limite && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Limite atingido</div>}
            </div>
          </div>
        )}

        {!suspensa && (
          <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Créditos avulsos de obra</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{plano.saldo_creditos_obra || 0}</div>
            </div>
            <button
              onClick={() => { setModalCredito(true); setQuantidadeCredito(1); setMetodoCredito('cartao'); setErroCredito(''); setPixCredito(null); setDadosCartaoCredito({ numero: '', nome: '', validade: '', cvv: '', cpf: '' }); }}
              style={{ padding: '8px 16px', background: 'none', border: `1px solid ${cor}`, color: cor, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              + Comprar crédito avulso
            </button>
          </div>
        )}

        {!suspensa && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600 }}>Funcionalidades incluídas</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {Object.entries(FEATURES_LABEL).map(([k, label]) => {
                const ativo = plano.funcionalidades?.[k];
                return (
                  <span key={k} style={{ fontSize: 13, padding: '5px 12px', borderRadius: 20, background: 'var(--bg-card)', color: ativo ? '#16a34a' : 'var(--text-muted)', border: `1px solid ${ativo ? '#86efac' : 'var(--border-color)'}` }}>
                    {ativo ? '✓' : '✗'} {label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {!suspensa && !ehTrial && ehAnual && (
            <button onClick={() => {
              const meu = planosDisponiveis.find(p => p.nome === plano.plano);
              if (!meu) return;
              setModalPagamento({ planoid: meu.planoid, nome: meu.nome, preco: meu.preco });
              setTelaCartao(true);
              setCicloEscolhido('mensal');
              setForcarMensal(true);
              setDadosCartao({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
              setErroCartao('');
              setMultaTroca(null);
              setCalculoUpgrade(null);
            }} style={{ padding: '8px 20px', background: 'none', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Mudar para mensal
            </button>
          )}
          {!suspensa && !ehTrial && !ehAnual && plano.status !== 'cancelado' && (
            <button onClick={() => {
              const meu = planosDisponiveis.find(p => p.nome === plano.plano);
              if (!meu) return;
              setModalPagamento({ planoid: meu.planoid, nome: meu.nome, preco: meu.preco });
              setTelaCartao(true);
              setCicloEscolhido('anual');
              setForcarMensal(false);
              setDadosCartao({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
              setErroCartao('');
              setMultaTroca(null);
              setCalculoUpgrade(null);
            }} style={{ padding: '8px 20px', background: 'none', border: '1px solid #16a34a', color: '#16a34a', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Mudar para anual (10% off)
            </button>
          )}
          {!suspensa && !ehTrial && plano.status !== 'cancelado' && (
            <button onClick={() => { setConfirmando(true); setMultaFidelidade(null); setErroCartao(''); setDadosCartao({ numero: '', nome: '', validade: '', cvv: '', cpf: '' }); }} style={{ padding: '8px 20px', background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Cancelar assinatura
            </button>
          )}
        </div>
      </div>

      {/* Planos disponíveis */}
      <div className="card">
        <h3 style={{ marginBottom: 8 }}>
          {ehTrial ? 'Escolha seu plano' : suspensa ? 'Reative sua conta, escolha um plano' : 'Trocar de plano'}
        </h3>
        {ehTrial && (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            A assinatura é feita aqui mesmo, com cartão de crédito (cobrança automática mensal) ou PIX.
          </p>
        )}
        {!suspensa && !ehTrial && (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Upgrade exige novo pagamento. Downgrade entra em vigor imediatamente: próxima cobrança já no novo valor.
          </p>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {planosDisponiveis.filter(p => p.nome !== 'gratis').map(p => {
            const pCor = PLANO_COR[p.nome] || '#7c3aed';
            const pBg  = PLANO_BG[p.nome]  || '#f5f3ff';
            const atual = p.nome === plano.plano && !suspensa;
            const funcionalidades = typeof p.funcionalidades === 'string' ? JSON.parse(p.funcionalidades) : p.funcionalidades;
            const isUpgrade = parseFloat(p.preco) > parseFloat(plano.preco);
            const isDowngrade = parseFloat(p.preco) < parseFloat(plano.preco);

            let labelBtn = 'Reativar com este plano';
            if (ehTrial) {
              labelBtn = 'Assinar agora';
            } else if (!suspensa) {
              if (isUpgrade) labelBtn = '⬆ Fazer upgrade';
              else if (isDowngrade) labelBtn = '⬇ Fazer downgrade';
              else labelBtn = 'Renovar plano';
            }

            return (
              <div key={p.planoid} style={{ flex: '1 1 180px', minWidth: 160, padding: 20, border: atual ? `2px solid ${pCor}` : '1px solid var(--border-color)', borderRadius: 12, background: 'var(--bg-card)', position: 'relative', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {atual && (
                  <span style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: pCor, color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>ATUAL</span>
                )}
                <div style={{ fontSize: 14, fontWeight: 700, color: pCor, textTransform: 'capitalize' }}>{p.nome}</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>
                  {p.nome === 'diamante' ? 'Sob consulta' : (
                    <>
                      R$ {parseFloat(p.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)' }}>/mês</span>
                    </>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                  <div>🏗️ {p.limite_obras ? `${p.limite_obras} obras simultâneas` : 'Obras simultâneas ilimitadas'}</div>
                  <div>👤 {p.limite_usuarios ? `${p.limite_usuarios} usuário${p.limite_usuarios > 1 ? 's' : ''}` : 'Usuários ilimitados'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
                  {Object.entries(FEATURES_LABEL).map(([k, label]) => (
                    <span key={k} style={{ color: funcionalidades?.[k] ? '#16a34a' : 'var(--text-muted)' }}>
                      {funcionalidades?.[k] ? '✓' : '✗'} {label}
                    </span>
                  ))}
                </div>
                {!atual && p.nome === 'diamante' && (
                  <a
                    href="https://wa.me/5544991096525?text=Ol%C3%A1%2C%20quero%20saber%20mais%20sobre%20o%20plano%20Diamante%20do%20CronoObra"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{ marginTop: 4, background: pCor, fontSize: 13, textAlign: 'center', textDecoration: 'none' }}
                  >
                    Fale conosco
                  </a>
                )}
                {!atual && p.nome !== 'diamante' && (
                  <button
                    className="btn-primary"
                    style={{ marginTop: 4, background: isDowngrade && !suspensa ? '#6b7280' : pCor, fontSize: 13 }}
                    onClick={() => {
                      if (!suspensa && isDowngrade && !ehAnual) {
                        setModalDowngrade({ planoid: p.planoid, nome: p.nome, preco: p.preco });
                        const problemas = [];
                        if (p.limite_obras && plano.limites.obras.uso > p.limite_obras) {
                          const excesso = plano.limites.obras.uso - p.limite_obras;
                          problemas.push(`Você tem ${plano.limites.obras.uso} obras ativas, mas o plano ${p.nome} suporta no máximo ${p.limite_obras}. Cancele ou finalize ${excesso} obra${excesso > 1 ? 's' : ''} antes de fazer o downgrade.`);
                        }
                        if (p.limite_usuarios && plano.limites.usuarios.uso > p.limite_usuarios) {
                          const excesso = plano.limites.usuarios.uso - p.limite_usuarios;
                          problemas.push(`Você tem ${plano.limites.usuarios.uso} usuários ativos, mas o plano ${p.nome} suporta no máximo ${p.limite_usuarios}. Remova ${excesso} usuário${excesso > 1 ? 's' : ''} antes de fazer o downgrade.`);
                        }
                        setErroDowngrade(problemas.join('\n\n'));
                      } else {
                        setModalPagamento({ planoid: p.planoid, nome: p.nome, preco: p.preco });
                        setTelaCartao(true);
                        setCicloEscolhido(ehAnual ? 'anual' : 'mensal');
                        setForcarMensal(false);
                        setDadosCartao({ numero: '', nome: '', validade: '', cvv: '', cpf: '' });
                        setErroCartao('');
                        setMultaTroca(null);
                        setCalculoUpgrade(null);
                        if (!suspensa && !ehTrial && isUpgrade) {
                          api.get(`/tenant/calcular-upgrade/${p.planoid}`)
                            .then(r => setCalculoUpgrade(r.data))
                            .catch(() => setCalculoUpgrade(null));
                        }
                      }
                    }}
                  >
                    {labelBtn}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de pagamento (upgrade / reativação) */}
      {modalPagamento && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            {(
              <>
                <h3 style={{ marginBottom: 4 }}>Dados do cartão</h3>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Plano <strong style={{ textTransform: 'capitalize' }}>{modalPagamento.nome}</strong>:{' '}
                  R$ {parseFloat(modalPagamento.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês
                </p>
                {forcarMensal && (
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
                    Trocando sua assinatura anual para <strong>mensal</strong>.
                  </p>
                )}
                {!forcarMensal && !ehTrial && !ehAnual && cicloEscolhido === 'anual' && (
                  <p style={{ fontSize: 12, color: '#16a34a', marginBottom: 14 }}>
                    Trocando sua assinatura mensal para <strong>anual (10% off)</strong>, cobrada 12x R$ {(parseFloat(modalPagamento.preco) * 0.9).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês.
                  </p>
                )}
                {!forcarMensal && (ehTrial || ehAnual) && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <button type="button" onClick={() => { setCicloEscolhido('mensal'); setMultaTroca(null); }}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `2px solid ${cicloEscolhido === 'mensal' ? '#2563eb' : 'var(--border-color)'}`, background: cicloEscolhido === 'mensal' ? '#eff6ff' : 'transparent', color: cicloEscolhido === 'mensal' ? '#2563eb' : 'var(--text-secondary)' }}>
                      Mensal
                      <div style={{ fontWeight: 400, fontSize: 11 }}>R$ {parseFloat(modalPagamento.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</div>
                    </button>
                    <button type="button" onClick={() => { setCicloEscolhido('anual'); setMultaTroca(null); }}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `2px solid ${cicloEscolhido === 'anual' ? '#2563eb' : 'var(--border-color)'}`, background: cicloEscolhido === 'anual' ? '#eff6ff' : 'transparent', color: cicloEscolhido === 'anual' ? '#2563eb' : 'var(--text-secondary)' }}>
                      Anual (10% off)
                      <div style={{ fontWeight: 400, fontSize: 11 }}>12x R$ {(parseFloat(modalPagamento.preco) * 0.9).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</div>
                    </button>
                  </div>
                )}
                {ehAnual && cicloEscolhido === 'mensal' && !multaTroca && (
                  <p style={{ fontSize: 12, color: '#d97706', marginBottom: 14 }}>
                    ⚠️ Sua assinatura anual ainda está na fidelidade. Trocar para o mensal pode gerar multa (calculamos ao confirmar).
                  </p>
                )}
                {multaTroca && (
                  <div style={{ background: '#7f1d1d', border: '1px solid #dc2626', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#fca5a5', lineHeight: 1.5 }}>⚠️ {multaTroca.mensagem}</p>
                  </div>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: calculoUpgrade?.mantem_vencimento ? 12 : 20 }}>
                  {cicloEscolhido === 'anual'
                    ? 'Assinatura anual, cobrança automática mensal no cartão. Cancelar antes de 12 meses gera multa de 10% sobre as parcelas restantes (ver Termos de Uso).'
                    : 'Assinatura recorrente, cobrança automática todo mês no cartão.'}
                </p>

                {calculoUpgrade?.mantem_vencimento && (
                  <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 13 }}>
                    <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>Cobrança deste upgrade</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: 'var(--text-secondary)' }}>
                      <span>{calculoUpgrade.dias_restantes} dias restantes no plano novo</span>
                      <span>R$ {calculoUpgrade.restante_novo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, color: '#16a34a' }}>
                      <span>Crédito dos dias já pagos no plano atual</span>
                      <span>− R$ {calculoUpgrade.restante_atual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14 }}>
                      <span>Cobrança agora</span>
                      <span style={{ color: 'var(--text-primary)' }}>R$ {calculoUpgrade.valor_cobrar.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                      A partir do próximo ciclo: R$ {calculoUpgrade.valor_cheio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês. A data de vencimento não muda.
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input placeholder="Número do cartão" value={dadosCartao.numero}
                    onChange={e => setDadosCartao(d => ({ ...d, numero: fmtCartao(e.target.value) }))}
                    style={{ fontSize: 16, letterSpacing: 2 }} />
                  <input placeholder="Nome no cartão (igual ao cartão)" value={dadosCartao.nome}
                    onChange={e => setDadosCartao(d => ({ ...d, nome: e.target.value.toUpperCase() }))} />
                  <div style={{ display: 'flex', gap: 12 }}>
                    <input placeholder="Validade MM/AA" value={dadosCartao.validade}
                      onChange={e => setDadosCartao(d => ({ ...d, validade: fmtValidade(e.target.value) }))}
                      style={{ flex: 1 }} maxLength={5} />
                    <input placeholder="CVV" value={dadosCartao.cvv}
                      onChange={e => setDadosCartao(d => ({ ...d, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      style={{ flex: 1 }} maxLength={4} />
                  </div>
                  <input placeholder="CPF do titular (somente números)" value={dadosCartao.cpf}
                    onChange={e => setDadosCartao(d => ({ ...d, cpf: fmtCPF(e.target.value) }))}
                    maxLength={14} />
                  {erroCartao && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{erroCartao}</p>}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button className="btn-secondary" style={{ flex: 1 }}
                    onClick={() => { setModalPagamento(null); setErroCartao(''); }} disabled={processando}>
                    Cancelar
                  </button>
                  <button className="btn-primary" style={{ flex: 2 }} onClick={() => iniciarCartao(!!multaTroca)} disabled={processando || !mpPronto}>
                    {processando ? 'Processando...' : multaTroca ? 'Pagar multa e confirmar' : 'Assinar agora'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal PIX */}
      {pagamentoPix && (
        <ModalPix
          pagamento={pagamentoPix}
          onConcluido={() => { setPagamentoPix(null); carregar(); window.location.reload(); }}
          onFechar={() => setPagamentoPix(null)}
        />
      )}

      {/* Modal downgrade */}
      {modalDowngrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginBottom: 12 }}>Confirmar downgrade?</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Você vai migrar do plano <strong style={{ textTransform: 'capitalize' }}>{plano.plano}</strong> para o plano{' '}
              <strong style={{ textTransform: 'capitalize' }}>{modalDowngrade.nome}</strong>.
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Novo valor: <strong>R$ {parseFloat(modalDowngrade.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</strong>
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: erroDowngrade ? 16 : 24 }}>
              Nenhum pagamento é necessário agora. A próxima cobrança já será no novo valor. Um e-mail de confirmação será enviado.
            </p>
            {erroDowngrade && (
              <div style={{ background: '#7f1d1d', border: '1px solid #dc2626', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
                {erroDowngrade.split('\n\n').map((linha, i) => (
                  <p key={i} style={{ margin: i > 0 ? '8px 0 0' : 0, fontSize: 13, color: '#fca5a5', lineHeight: 1.5 }}>
                    ⚠️ {linha}
                  </p>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => { setModalDowngrade(null); setErroDowngrade(''); }} disabled={processandoDowngrade}>
                Cancelar
              </button>
              <button className="btn-primary" style={{ flex: 2, opacity: (processandoDowngrade || !!erroDowngrade) ? 0.4 : 1, cursor: (processandoDowngrade || !!erroDowngrade) ? 'not-allowed' : 'pointer', pointerEvents: (processandoDowngrade || !!erroDowngrade) ? 'none' : 'auto' }} onClick={confirmarDowngrade} disabled={processandoDowngrade || !!erroDowngrade}>
                {processandoDowngrade ? 'Alterando...' : 'Confirmar downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de sucesso */}
      {modalSucesso && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 40, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>
              {modalSucesso.pendente ? '⏳' : '✅'}
            </div>
            <h3 style={{ marginBottom: 12, fontSize: 22 }}>{modalSucesso.titulo}</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 28 }}>
              {modalSucesso.texto}
            </p>
            <button className="btn-primary" style={{ width: '100%' }} onClick={() => setModalSucesso(null)}>
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal renovação de fidelidade anual vencida */}
      {etapaRenovacaoAnual && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, maxWidth: 440, width: '90%' }}>
            {etapaRenovacaoAnual === 'anual' ? (
              <>
                <h3 style={{ marginBottom: 12 }}>Sua fidelidade anual venceu</h3>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
                  Deseja renovar por mais 12 meses no novo valor de <strong>R$ {parseFloat(plano?.renovacao_anual_novo_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</strong> (preço do plano {plano?.plano} reajustado, com 10% de desconto)? Nada é cobrado agora, o novo valor só vale a partir da próxima cobrança.
                </p>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button className="btn-secondary" style={{ flex: 1 }} disabled={processandoRenovacaoAnual} onClick={() => setEtapaRenovacaoAnual('mensal')}>Não</button>
                  <button className="btn-primary" style={{ flex: 1 }} disabled={processandoRenovacaoAnual} onClick={confirmarRenovacaoAnual}>
                    {processandoRenovacaoAnual ? 'Processando...' : 'Renovar anual'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: 12 }}>Deseja seguir no plano mensal?</h3>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
                  Sem desconto e sem fidelidade, o plano {plano?.plano} mensal custa <strong>R$ {parseFloat(planosDisponiveis.find(p => p.nome === plano?.plano)?.preco || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</strong>. Se preferir não continuar, sua assinatura será cancelada (acesso mantido até o fim do período já pago).
                </p>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button className="btn-danger" style={{ flex: 1 }} disabled={processandoRenovacaoAnual} onClick={cancelarPosFidelidade}>Não, cancelar</button>
                  <button className="btn-primary" style={{ flex: 1 }} disabled={processandoRenovacaoAnual} onClick={confirmarTrocaParaMensalPosFidelidade}>
                    {processandoRenovacaoAnual ? 'Processando...' : 'Sim, seguir mensal'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal comprar crédito avulso */}
      {modalCredito && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%' }}>
            {pixCredito ? (
              <>
                <h3 style={{ marginBottom: 12 }}>Pague com PIX</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Escaneie o QR Code ou copie o código abaixo. Assim que o pagamento for confirmado, o saldo de créditos é atualizado automaticamente.
                </p>
                {pixCredito.qr_code_base64 && (
                  <img src={`data:image/png;base64,${pixCredito.qr_code_base64}`} alt="QR Code PIX" style={{ width: '100%', maxWidth: 240, display: 'block', margin: '0 auto 16px' }} />
                )}
                <textarea readOnly value={pixCredito.qr_code || ''} rows={3} style={{ resize: 'none', fontSize: 11, marginBottom: 16 }} onClick={e => e.target.select()} />
                <button className="btn-secondary" style={{ width: '100%' }} onClick={() => { setModalCredito(false); setPixCredito(null); carregar(); }}>Fechar</button>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: 12 }}>Comprar crédito avulso</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Cada crédito libera 1 obra extra, permanentemente, além do limite do seu plano. Preço unitário: <strong>R$ {parseFloat(plano.preco_credito ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>.
                </p>
                <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Quantidade</label>
                <input type="number" min={1} value={quantidadeCredito}
                  onChange={e => setQuantidadeCredito(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{ marginBottom: 16 }} />
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  <button onClick={() => setMetodoCredito('cartao')} style={{ flex: 1, padding: 10, borderRadius: 8, border: `1px solid ${metodoCredito === 'cartao' ? cor : 'var(--border-color)'}`, background: metodoCredito === 'cartao' ? `${cor}22` : 'none', color: metodoCredito === 'cartao' ? cor : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>Cartão</button>
                  <button onClick={() => setMetodoCredito('pix')} style={{ flex: 1, padding: 10, borderRadius: 8, border: `1px solid ${metodoCredito === 'pix' ? cor : 'var(--border-color)'}`, background: metodoCredito === 'pix' ? `${cor}22` : 'none', color: metodoCredito === 'pix' ? cor : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>PIX</button>
                </div>
                {metodoCredito === 'cartao' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    <input placeholder="Número do cartão" value={dadosCartaoCredito.numero}
                      onChange={e => setDadosCartaoCredito(d => ({ ...d, numero: fmtCartao(e.target.value) }))}
                      style={{ fontSize: 15, letterSpacing: 1 }} />
                    <input placeholder="Nome no cartão" value={dadosCartaoCredito.nome}
                      onChange={e => setDadosCartaoCredito(d => ({ ...d, nome: e.target.value.toUpperCase() }))} />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input placeholder="Validade MM/AA" value={dadosCartaoCredito.validade}
                        onChange={e => setDadosCartaoCredito(d => ({ ...d, validade: fmtValidade(e.target.value) }))}
                        style={{ flex: 1 }} maxLength={5} />
                      <input placeholder="CVV" value={dadosCartaoCredito.cvv}
                        onChange={e => setDadosCartaoCredito(d => ({ ...d, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                        style={{ flex: 1 }} maxLength={4} />
                    </div>
                    <input placeholder="CPF do titular" value={dadosCartaoCredito.cpf}
                      onChange={e => setDadosCartaoCredito(d => ({ ...d, cpf: fmtCPF(e.target.value) }))}
                      maxLength={14} />
                  </div>
                )}
                {erroCredito && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 16 }}>{erroCredito}</p>}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => setModalCredito(false)} className="btn-secondary" style={{ flex: 1 }}>Cancelar</button>
                  <button onClick={comprarCredito} disabled={processandoCredito || (metodoCredito === 'cartao' && !mpPronto)} className="btn-primary" style={{ flex: 1 }}>
                    {processandoCredito ? 'Processando...' : 'Confirmar compra'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal cancelamento */}
      {confirmando && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%' }}>
            <h3 style={{ marginBottom: 12 }}>Cancelar assinatura?</h3>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 8 }}>
              Seu acesso continuará até <strong>{plano.data_vencimento ? fmtData(plano.data_vencimento) : 'o fim do período'}</strong>.
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: multaFidelidade ? 16 : 24 }}>
              Após essa data o acesso será encerrado. Todos os dados serão mantidos por 60 dias.
            </p>
            {multaFidelidade && (
              <>
                <div style={{ background: '#7f1d1d', border: '1px solid #dc2626', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#fca5a5', lineHeight: 1.5 }}>
                    ⚠️ {multaFidelidade.mensagem}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                  <input placeholder="Número do cartão" value={dadosCartao.numero}
                    onChange={e => setDadosCartao(d => ({ ...d, numero: fmtCartao(e.target.value) }))}
                    style={{ fontSize: 15, letterSpacing: 1 }} />
                  <input placeholder="Nome no cartão" value={dadosCartao.nome}
                    onChange={e => setDadosCartao(d => ({ ...d, nome: e.target.value.toUpperCase() }))} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input placeholder="Validade MM/AA" value={dadosCartao.validade}
                      onChange={e => setDadosCartao(d => ({ ...d, validade: fmtValidade(e.target.value) }))}
                      style={{ flex: 1 }} maxLength={5} />
                    <input placeholder="CVV" value={dadosCartao.cvv}
                      onChange={e => setDadosCartao(d => ({ ...d, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      style={{ flex: 1 }} maxLength={4} />
                  </div>
                  <input placeholder="CPF do titular" value={dadosCartao.cpf}
                    onChange={e => setDadosCartao(d => ({ ...d, cpf: fmtCPF(e.target.value) }))}
                    maxLength={14} />
                  {erroCartao && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{erroCartao}</p>}
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => { setConfirmando(false); setMultaFidelidade(null); setErroCartao(''); }} className="btn-secondary" style={{ flex: 1 }}>Voltar</button>
              <button onClick={() => handleCancelar(!!multaFidelidade)} disabled={cancelando} style={{ flex: 1, padding: '10px', background: '#ef4444', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: cancelando ? 'not-allowed' : 'pointer', color: '#fff', opacity: cancelando ? 0.7 : 1 }}>
                {cancelando ? 'Processando...' : multaFidelidade ? 'Pagar multa e cancelar' : 'Confirmar cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Plano;
